import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { takeLatestTenantRows } from '../../services/eventSystem/actionCacheScope';
import useEventSystemFixed from '../../hooks/useEventSystemFixed';
import { useAuth } from '../../contexts/AuthContext';

// Create context
const EventSystemContext = createContext();

// Custom hook to use the event system context
export const useEventSystemData = () => {
  const context = useContext(EventSystemContext);
  if (!context) {
    throw new Error('useEventSystemData must be used within an EventSystemProvider');
  }
  return context;
};

// Provider component
export const EventSystemProviderFixed = ({ children }) => {
  const { fetchFlows, fetchActions, fetchDevices, loading, error, tenantId, clearCache } =
    useEventSystemFixed();
  const { authReady, currentUser } = useAuth();

  const [flows, setFlows] = useState([]);
  const [actions, setActions] = useState([]);
  const [devices, setDevices] = useState([]);

  const normalizeDevice = (d = {}) => ({
    ...d,
    label: d.label ?? d.name ?? 'Unnamed Device',
    deviceType: d.deviceType ?? d.type ?? 'other',
    isActive: typeof d.isActive === 'boolean' ? d.isActive : (typeof d.active === 'boolean' ? d.active : true),
    agentId: d.agentId ?? d.agent_id ?? d.agentID ?? '',
    ipAddress: d.ipAddress ?? d.ip ?? d.ip_address ?? '',
    capabilities: Array.isArray(d.capabilities) ? d.capabilities : [],
    targets: d.targets || {},
  });
  const upsertDevice = (device) => {
    if (!device) return;
    const n = normalizeDevice(device);
    setDevices((prev) => {
      const idx = prev.findIndex((d) => (d?.id ?? d?._id) === (n?.id ?? n?._id));
      if (idx === -1) return [...prev, n];
      const next = prev.slice();
      next[idx] = { ...prev[idx], ...n };
      return next;
    });
  };
  const deleteDeviceById = (id) => {
    if (!id) return;
    setDevices((prev) => prev.filter((d) => (d?.id ?? d?._id) !== id));
  };

  // Circuit breaker state to prevent continuous retries
  const [lastFetchAttempt, setLastFetchAttempt] = useState(null);
  const [hasInitialized, setHasInitialized] = useState(false);

  // Track the last tenant ID to detect changes
  const [lastTenantId, setLastTenantId] = useState(null);
  const loadGen = useRef(0);

  // Fetch data when component mounts or tenant changes
  useEffect(() => {
    // Mark provider as active so other layers can avoid duplicate fetching
    if (typeof window !== 'undefined') {
      window.__ES_PROVIDER_ACTIVE = true;
    }

    return () => {
      if (typeof window !== 'undefined') {
        window.__ES_PROVIDER_ACTIVE = false;
      }
    };
  }, []);

  // Fetch data when component mounts or tenant changes
  useEffect(() => {
    const loadData = async () => {
      const gen = ++loadGen.current;
      const applyRows = (rows) => takeLatestTenantRows(gen, loadGen.current, rows);
      if (!authReady || !currentUser) {
        return;
      }

      if (!tenantId) {
        setFlows([]);
        setActions([]);
        setDevices([]);
        setHasInitialized(true);
        setLastTenantId(null);
        return;
      }

      const tenantChanged = Boolean(lastTenantId && lastTenantId !== tenantId);
      if (tenantChanged) {
        setFlows([]);
        setActions([]);
        setDevices([]);
        clearCache();
        setHasInitialized(false);
        setLastFetchAttempt(null);
      }

      if (!tenantChanged && hasInitialized && lastTenantId === tenantId) {
        return;
      }

      const now = Date.now();
      if (!tenantChanged && lastFetchAttempt && now - lastFetchAttempt < 5000) {
        return;
      }

      setFlows([]);
      setActions([]);
      setDevices([]);
      setLastFetchAttempt(now);
      setLastTenantId(tenantId);

      try {
        const flowsData = await fetchFlows();
        const nextFlows = applyRows(flowsData);
        if (nextFlows !== null) setFlows(nextFlows);
      } catch {
        if (applyRows([]) !== null) setFlows([]);
      }

      try {
        const actionsData = await fetchActions();
        const nextActions = applyRows(actionsData);
        if (nextActions !== null) setActions(nextActions);
      } catch {
        if (applyRows([]) !== null) setActions([]);
      }

      try {
        const devicesData = await fetchDevices();
        const nextDevices = applyRows(devicesData);
        if (!nextDevices) return;
        const seenMacs = new Set();
        const seenLabels = new Set();
        const seenIds = new Set();
        const deduplicatedDevices = nextDevices.filter(d => {
          const mac = (d.mac || d.macAddress || d.mac_address || '').toUpperCase().replace(/[^A-F0-9]/g, '');
          const id = d.id || d.deviceId || d._id;
          const label = (d.name || d.label || d.deviceName || id || '').toLowerCase().trim();
          if (mac && mac.length >= 12) {
            if (seenMacs.has(mac)) return false;
            seenMacs.add(mac);
            if (label) seenLabels.add(label);
            if (id) seenIds.add(id);
            return true;
          }
          if (label && seenLabels.has(label)) return false;
          if (label) seenLabels.add(label);
          if (!id || seenIds.has(id)) return false;
          seenIds.add(id);
          return true;
        });
        if (applyRows(deduplicatedDevices) !== null) setDevices(deduplicatedDevices);
      } catch {
        if (applyRows([]) !== null) setDevices([]);
      }

      if (applyRows([]) !== null) setHasInitialized(true);
    };

    loadData();
    return () => {
      loadGen.current += 1;
    };
  }, [tenantId, authReady, currentUser?.uid]);

  // Function to refresh all data
  const refreshData = async () => {
    const generation = loadGen.current;
    const applyRows = (rows) => takeLatestTenantRows(generation, loadGen.current, rows);
    setFlows([]);
    setActions([]);
    setDevices([]);
    clearCache();

    if (tenantId) {
      if (process.env.NODE_ENV !== 'production') {
        console.log(`EventSystemProviderFixed: Refreshing data for tenant ${tenantId}`);
      }

      try {
        if (process.env.NODE_ENV !== 'production') {
          console.log('EventSystemProviderFixed: Refreshing flows...');
        }
        const flowsData = await fetchFlows(true);
        const nextFlows = applyRows(flowsData);
        if (nextFlows !== null) setFlows(nextFlows);
      } catch (err) {
        console.error('EventSystemProviderFixed: Error refreshing flows:', err);
        if (applyRows([]) !== null) setFlows([]);
      }

      try {
        if (process.env.NODE_ENV !== 'production') {
          console.log('EventSystemProviderFixed: Refreshing actions...');
        }
        const actionsData = await fetchActions(true);
        const nextActions = applyRows(actionsData);
        if (nextActions !== null) setActions(nextActions);
      } catch (err) {
        console.error('EventSystemProviderFixed: Error refreshing actions:', err);
        if (applyRows([]) !== null) setActions([]);
      }

      try {
        if (process.env.NODE_ENV !== 'production') {
          console.log('EventSystemProviderFixed: Refreshing devices...');
        }
        const devicesData = await fetchDevices(true);
        if (process.env.NODE_ENV !== 'production') {
          console.log(`EventSystemProviderFixed: Refreshed ${devicesData?.length || 0} devices`);
        }
        // Deduplicate by: 1) MAC address, 2) display label, 3) ID
        const seenMacs = new Set();
        const seenLabels = new Set();
        const seenIds = new Set();
        const deduplicatedDevices = (devicesData || []).filter(d => {
          const mac = (d.mac || d.macAddress || d.mac_address || '').toUpperCase().replace(/[^A-F0-9]/g, '');
          const id = d.id || d.deviceId || d._id;
          const label = (d.name || d.label || d.deviceName || id || '').toLowerCase().trim();
          if (mac && mac.length >= 12) {
            if (seenMacs.has(mac)) return false;
            seenMacs.add(mac);
            if (label) seenLabels.add(label);
            if (id) seenIds.add(id);
            return true;
          }
          if (label && seenLabels.has(label)) return false;
          if (label) seenLabels.add(label);
          if (!id || seenIds.has(id)) return false;
          seenIds.add(id);
          return true;
        });
        const nextDevices = applyRows(deduplicatedDevices);
        if (nextDevices !== null) setDevices(nextDevices);
      } catch (err) {
        console.error('EventSystemProviderFixed: Error refreshing devices:', err);
        if (applyRows([]) !== null) setDevices([]);
      }
    } else {
      if (process.env.NODE_ENV !== 'production') {
        console.warn('EventSystemProviderFixed: No tenant ID available for refresh');
      }
    }
  };


  // Value to provide through context
  const value = {
    flows,
    actions,
    devices,
    loading,
    error,
    tenantId,
    refreshData,
    upsertDevice,
    deleteDeviceById,
  };

  return <EventSystemContext.Provider value={value}>{children}</EventSystemContext.Provider>;
};

export default EventSystemProviderFixed;
